import { useEffect, useState } from "react";
import {
    Smile, MapPin, Loader2, CheckCircle2, ImagePlus, AlertCircle, Smartphone, ChevronDown, QrCode,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";

/**
 * Phase 4.18 — Smily-Aktion: Fahrzeug melden
 *
 * Vermittler meldet ein am Straßenrand stehendes Fahrzeug. Workflow:
 *   1. Foto 1 — Seitenansicht vom Fahrzeug
 *   2. Foto 2 — Wo der Smily-Aufkleber aufgeklebt wurde
 *   3. Automatische Standorterkennung (Browser-Geolocation API)
 *   4. Optional: Marke / Modell / Notiz
 *   5. Checkbox „Auch in mobiler App nutzbar"
 *   6. Submit -> POST /api/vermittler/smily-reports
 */
const MAX_PHOTO_BYTES = 6 * 1024 * 1024;

function PhotoSlot({ label, hint, url, uploading, onUpload, testIdPrefix }) {
    return (
        <div className="border-2 border-dashed border-gray-300 rounded-sm p-3 bg-gray-50" data-testid={`${testIdPrefix}-slot`}>
            <Label className="text-[11px] uppercase tracking-wider font-bold mb-1.5 block">{label}</Label>
            <p className="text-[10px] text-gray-500 mb-2">{hint}</p>
            {url ? (
                <div className="relative">
                    <img src={url} alt={label} className="w-full h-32 object-cover rounded-sm" data-testid={`${testIdPrefix}-preview`} />
                    <span className="absolute top-1 right-1 bg-green-600 text-white rounded-full p-1">
                        <CheckCircle2 className="w-3 h-3" />
                    </span>
                </div>
            ) : (
                <label className={`flex flex-col items-center justify-center h-32 cursor-pointer text-gray-500 hover:text-[#111827] hover:bg-white rounded-sm transition-colors ${uploading ? "opacity-50 cursor-wait" : ""}`} data-testid={`${testIdPrefix}-input-label`}>
                    {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImagePlus className="w-6 h-6" />}
                    <span className="text-[11px] font-bold mt-1.5">{uploading ? "Lade…" : "Foto wählen"}</span>
                    <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        disabled={uploading}
                        onChange={(e) => { onUpload(e.target.files?.[0]); e.target.value = ""; }}
                        className="hidden"
                        data-testid={`${testIdPrefix}-input`}
                    />
                </label>
            )}
        </div>
    );
}

export default function SmilyReportCard({ transactionCode, shareUrl }) {
    const [form, setForm] = useState({
        photo_side_url: "", photo_sticker_url: "",
        latitude: null, longitude: null, address: "",
        brand: "", model: "", note: "", mobile_app_enabled: false,
    });
    const [uploadingSide, setUploadingSide] = useState(false);
    const [uploadingSticker, setUploadingSticker] = useState(false);
    const [locating, setLocating] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [history, setHistory] = useState([]);
    const [formOpen, setFormOpen] = useState(false);
    const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const loadHistory = async () => {
        try {
            const r = await api.get("/vermittler/smily-reports");
            setHistory(r.data.items || []);
        } catch { /* offline */ }
    };
    useEffect(() => { loadHistory(); }, []);

    const uploadPhoto = async (file, target, setUploading) => {
        if (!file) return;
        if (file.size > MAX_PHOTO_BYTES) {
            toast.error(`Datei zu groß (max ${MAX_PHOTO_BYTES / 1024 / 1024} MB).`);
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
            update(target, r.data.url);
            toast.success("Foto hochgeladen");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Upload fehlgeschlagen");
        } finally {
            setUploading(false);
        }
    };

    const detectLocation = () => {
        if (!navigator.geolocation) {
            toast.error("Browser unterstützt keine Standorterkennung.");
            return;
        }
        setLocating(true);
        navigator.geolocation.getCurrentPosition(
            async (pos) => {
                const lat = pos.coords.latitude;
                const lng = pos.coords.longitude;
                update("latitude", lat);
                update("longitude", lng);
                // Reverse-geocode via OpenStreetMap Nominatim (rate-limited, best-effort)
                try {
                    const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`, {
                        headers: { "Accept": "application/json" },
                    });
                    if (res.ok) {
                        const j = await res.json();
                        if (j.display_name) update("address", j.display_name);
                    }
                } catch { /* ignore — address optional */ }
                setLocating(false);
                toast.success("Standort erfasst");
            },
            (err) => {
                setLocating(false);
                toast.error(err.code === 1 ? "Standort-Berechtigung verweigert" : "Standort konnte nicht ermittelt werden");
            },
            { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 },
        );
    };

    const submit = async () => {
        if (!form.photo_side_url || !form.photo_sticker_url) {
            toast.error("Bitte beide Fotos (Seite + Aufkleber) hochladen");
            return;
        }
        if (form.latitude == null || form.longitude == null) {
            toast.error("Bitte Standort erfassen");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/vermittler/smily-reports", form);
            toast.success("Smily-Meldung übermittelt");
            setForm({
                photo_side_url: "", photo_sticker_url: "",
                latitude: null, longitude: null, address: "",
                brand: "", model: "", note: "", mobile_app_enabled: form.mobile_app_enabled,
            });
            await loadHistory();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Übermittlung fehlgeschlagen");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="bg-white border border-gray-200 rounded-sm" data-testid="smily-report-card">
            <button
                type="button"
                onClick={() => setFormOpen((o) => !o)}
                className="w-full flex items-center gap-3 p-5 text-left hover:bg-gray-50 transition-colors"
                aria-expanded={formOpen}
                data-testid="smily-toggle"
            >
                <Smile className="w-5 h-5 text-[#FFCC00] shrink-0" />
                <div className="flex-1 min-w-0">
                    <h3 className="font-display font-bold text-base">Smily-Aktion · Fahrzeug melden</h3>
                    <p className="text-xs text-gray-600 mt-0.5">
                        {formOpen
                            ? "Foto + Smily-Aufkleber + Standort = Smily-Meldung. Bei erfolgreicher Vermittlung Punkte-Gutschrift."
                            : `${history.length} Meldung${history.length === 1 ? "" : "en"} übermittelt — Klicken zum Melden`}
                    </p>
                </div>
                <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform shrink-0 ${formOpen ? "rotate-180" : ""}`} />
            </button>

            {formOpen && (
            <div className="px-6 pb-6" data-testid="smily-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                <PhotoSlot
                    label="Foto 1 · Seitenansicht"
                    hint="Komplette Seite vom Fahrzeug sichtbar"
                    url={form.photo_side_url}
                    uploading={uploadingSide}
                    onUpload={(f) => uploadPhoto(f, "photo_side_url", setUploadingSide)}
                    testIdPrefix="smily-photo-side"
                />
                <PhotoSlot
                    label="Foto 2 · Smily-Aufkleber"
                    hint="Wo wurde der Aufkleber platziert?"
                    url={form.photo_sticker_url}
                    uploading={uploadingSticker}
                    onUpload={(f) => uploadPhoto(f, "photo_sticker_url", setUploadingSticker)}
                    testIdPrefix="smily-photo-sticker"
                />
            </div>

            <div className="border border-gray-200 rounded-sm p-3 mb-4 bg-gray-50">
                <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold text-gray-700">
                            <MapPin className="w-3.5 h-3.5" /> Standort
                        </div>
                        {form.latitude != null ? (
                            <div className="mt-1" data-testid="smily-location-coords">
                                <div className="text-xs font-mono-num text-[#111827]">
                                    {form.latitude.toFixed(5)}, {form.longitude.toFixed(5)}
                                </div>
                                {form.address && (
                                    <div className="text-[11px] text-gray-600 mt-0.5" data-testid="smily-location-address">{form.address}</div>
                                )}
                            </div>
                        ) : (
                            <p className="text-[11px] text-gray-500 mt-1">Noch nicht erfasst — bitte „Standort erfassen" drücken.</p>
                        )}
                    </div>
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={detectLocation}
                        disabled={locating}
                        className="rounded-sm font-bold uppercase tracking-wider shrink-0"
                        data-testid="smily-detect-location"
                    >
                        {locating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MapPin className="w-3.5 h-3.5 mr-1" />}
                        {locating ? "Ortet…" : "Standort erfassen"}
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <div>
                    <Label className="text-[11px] uppercase tracking-wider font-bold">Marke (optional)</Label>
                    <Input value={form.brand} onChange={(e) => update("brand", e.target.value)} placeholder="z. B. Vespa" className="rounded-sm" data-testid="smily-brand" />
                </div>
                <div>
                    <Label className="text-[11px] uppercase tracking-wider font-bold">Modell (optional)</Label>
                    <Input value={form.model} onChange={(e) => update("model", e.target.value)} placeholder="z. B. GTS 300" className="rounded-sm" data-testid="smily-model" />
                </div>
            </div>
            <div className="mb-3">
                <Label className="text-[11px] uppercase tracking-wider font-bold">Notiz (optional)</Label>
                <Textarea value={form.note} onChange={(e) => update("note", e.target.value)} rows={2} placeholder="z. B. Steht seit Wochen am gleichen Platz, sieht verlassen aus" className="rounded-sm text-sm" data-testid="smily-note" />
            </div>

            <label className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-sm mb-4 cursor-pointer" data-testid="smily-mobile-app-toggle">
                <input
                    type="checkbox"
                    checked={form.mobile_app_enabled}
                    onChange={(e) => update("mobile_app_enabled", e.target.checked)}
                    className="mt-1 w-4 h-4 accent-[#FFCC00]"
                    data-testid="smily-mobile-app-checkbox"
                />
                <div className="flex-1">
                    <div className="flex items-center gap-1.5 font-bold text-sm">
                        <Smartphone className="w-4 h-4" /> Auch in mobiler App nutzbar
                    </div>
                    <div className="text-[11px] text-gray-600 mt-0.5">
                        Diese Meldung wird auch in Ihrer TwoWheels-Mobile-App freigeschaltet (Kamera + GPS direkt aufrufen).
                    </div>
                </div>
            </label>

            <Button
                onClick={submit}
                disabled={submitting}
                className="w-full bg-[#111827] hover:bg-black text-white font-bold rounded-sm uppercase tracking-wider"
                data-testid="smily-submit"
            >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Smily-Meldung übermitteln"}
            </Button>
            </div>
            )}

            {/* QR-Smiley-Werbemittel — zu einer Ansicht mit der Smily-Aktion verbunden (User-Wunsch) */}
            {(transactionCode || shareUrl) && (
                <div className="px-6 pb-6 pt-4 border-t border-gray-100" data-testid="smily-werbemittel">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-2 flex items-center gap-1.5">
                        <QrCode className="w-3.5 h-3.5 text-[#FFCC00]" /> QR-Smiley-Werbemittel — für Aufkleber & Aufsteller
                    </div>
                    <div className="flex flex-wrap items-center gap-4 bg-gray-50 border border-gray-200 rounded-sm p-3">
                        <div className="bg-white border-2 border-[#111827] rounded-sm px-3 py-2 text-center shrink-0">
                            <Smile className="w-8 h-8 text-[#FFCC00] mx-auto" />
                            <div className="font-mono-num font-black text-xs mt-1">{transactionCode || "—"}</div>
                        </div>
                        {shareUrl && (
                            <div className="bg-white border border-gray-200 rounded-sm p-2 shrink-0" data-testid="smily-qr-wrap">
                                <QRCodeSVG value={shareUrl} size={96} level="H" fgColor="#111827" bgColor="#FFFFFF" />
                            </div>
                        )}
                        <div className="min-w-0 flex-1">
                            <p className="text-xs text-gray-700 font-bold">Printbares Smiley-Logo + dynamischer QR-Code</p>
                            <p className="text-[11px] text-gray-500 mt-0.5">
                                Käufer scannen den Code → landen auf deinem Empfehlungs-Link. Jeder Verkauf darüber zählt für dich.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Feb-2026: Übermittelte Smily-Meldungen — immer sichtbar, auch wenn Form kollabiert */}
            <div className="px-6 pb-6 pt-4 border-t border-gray-100">
                <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-2">
                    Übermittelte Smily-Meldungen
                </div>
                {history.length === 0 ? (
                    <div className="text-xs text-gray-500 flex items-center gap-1.5" data-testid="smily-empty">
                        <AlertCircle className="w-3 h-3" /> Noch keine Meldungen
                    </div>
                ) : (
                    <ul className="divide-y divide-gray-100 max-h-48 overflow-auto" data-testid="smily-history">
                        {history.slice(0, 8).map((r) => (
                            <li key={r.id} className="py-2 flex items-start gap-2" data-testid={`smily-history-item-${r.id}`}>
                                <img src={r.photo_side_url} alt="Seite" className="w-10 h-10 object-cover rounded-sm shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-bold truncate">{r.brand || "Unbekannte Marke"} {r.model}</div>
                                    <div className="text-[11px] text-gray-500 truncate">{r.address || `${r.latitude?.toFixed(3)}, ${r.longitude?.toFixed(3)}`}</div>
                                    <div className="text-[10px] text-gray-400 font-mono-num">{new Date(r.created_at).toLocaleString("de-DE")}</div>
                                </div>
                                <span className="text-[10px] uppercase tracking-wider font-bold text-amber-700 shrink-0">{r.status}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}
